/** @jsxRuntime classic */
/** @jsx jsx */
/*
License

Copyright (c) 2022 Chukwubuikem Ume-Ugwa

Permission is hereby witheld, any person obtaining a copy
of this software and associated documentation files (the "Software"), cannot
use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software. These restrictions can be only lifted with
a written permission from Chukwubuikem Ume-Ugwa.
*/
import { css, jsx } from '@emotion/react';
import { useScroll } from '@use-gesture/react';
import { useCallback, useRef, useState, useEffect } from 'react';
import {
  Button,
  Container,
  Flex,
  Image,
  Box,
  VStack,
  HStack,
  Circle,
  Fade,
  Text,
} from '@chakra-ui/react';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { log } from '../utils';

export function HorizontalScroll({ Item, header, data, idKey }) {
  const [left, setLeft] = useState(false);
  const [overflow, setOverflow] = useState(false);
  const [scrollLeft, setScrollLeft] = useState(0);

  const [containerBounds, setContainerBounds] = useState({
    offsetTop: 0,
    clientHeight: 0,
    clientWidth: 0,
  });
  const containerRef = useRef();
  const computeTotalVisible = (itemWidth, containerWidth) => {
    const totalVisible = Math.ceil(containerWidth / itemWidth);
    return totalVisible;
  };

  const computeOffscreenLeftEdge = (itemCount, itemWidth, containerWidth) => {
    const totalVisible = computeTotalVisible(itemWidth, containerWidth);
    const fractionOffscreen = totalVisible - containerWidth / itemWidth;
    const itemCountBeforeEdge = itemCount - totalVisible;

    const ans = Math.abs(
      itemCountBeforeEdge * itemWidth + fractionOffscreen * itemWidth
    );
    log({
      component: 'HorizontalScroll',
      itemCountBeforeEdge: itemCountBeforeEdge,
      totalVisible: totalVisible,
      fractionOffscreen: fractionOffscreen,
      itemCount: itemCount,
      edge: ans,
    });
    return ans;
  };

  const handleScroll = useCallback(
    event => {
      const newX = event.xy[0];
      const edge = computeOffscreenLeftEdge(
        data.length,
        event.target.lastChild.clientWidth,
        event.target.clientWidth
      );
      log({
        component: 'HorizontalScroll',
        edge: edge,
        itemWidth: event.target.lastChild.clientWidth || 0,
        containerWidth: event.target.clientWidth || 0,
      });
      if (newX < edge && event.delta[0] > 0) {
        setLeft(false);
        return;
      }

      if (event.delta[0] < 0) {
        setLeft(true && newX > 0);
        return;
      }
      setLeft(true);
    },
    [data]
  );

  const bind = useScroll(event => {
    if (event.scrolling) {
      handleScroll(event);
    }
  });

  const handleScrollLeft = () => {
    setScrollLeft(scrollLeft - containerBounds.clientWidth);
    containerRef.current.scroll({
      left: scrollLeft - containerBounds.clientWidth,
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleScrollRight = () => {
    setScrollLeft(scrollLeft + containerBounds.clientWidth);
    containerRef.current.scroll({
      left: scrollLeft + containerBounds.clientWidth,
      top: 0,
      behavior: 'smooth',
    });
  };

  const placeholder = useCallback(key => {
    return (
      <div key={key}>
        <Container
          key={key}
          css={css`
            flex-shrink: 0;
            background-size: cover;
            background-repeat: no-repeat;
            background-position: 'center center';
            background-color: #171923;
            padding: 10px;
            &:hover {
              box-shadow: 1px 1px 1px 1px #171923;
            }
          `}
          height={['195px', '250px', '255px', '355px', '255px']}
          width={['190px', '250px', '250px', '350px', '300px']}
          borderRadius={'2xl'}
          marginLeft={['13px', '13px']}
        >
          <VStack>
            <Image
              borderRadius={'2xl'}
              height={['90px', '150px', '150px', '250px', '150px']}
              width={['90px', '150px', '150px', '250px', '150px']}
              src={`${process.env.PUBLIC_URL}/logo-512x512-bg.png`}
            />
            <HStack align={'end'} m={[1, 1, 2, 8]}>
              <Box
                bg="yellow.400"
                w={['50px', '60px', '60px', '30px', '50px']}
                p={1}
                color="white"
              ></Box>
              <Box
                bg="yellow.400"
                w={['50px', '60px', '60px', '30px', '50px']}
                p={1}
                color="white"
              ></Box>
            </HStack>
            <Box bg="yellow.400" w="100%" p={1} color="white"></Box>
          </VStack>
          <Flex position={'relative'} top={['13px']}>
            <Button
              disabled
              bg={'yellow.400'}
              color={'black'}
              width={'40px'}
              height={'40px'}
              borderRadius={'50%'}
            >
              <span class="material-icons md-48">play_arrow</span>
            </Button>
          </Flex>
        </Container>
      </div>
    );
  }, []);

  useEffect(() => {
    const totalVisible = computeTotalVisible(
      containerRef.current.lastChild.clientWidth,
      containerRef.current.clientWidth
    );
    setOverflow(data.length > totalVisible);
  }, [data.length]);

  useEffect(() => {
    let { offsetTop, clientHeight, clientWidth } = containerRef.current;
    setContainerBounds({
      offsetTop: offsetTop + clientHeight * 0.4,
      clientHeight: clientHeight,
      clientWidth: clientWidth,
    });
  }, [containerRef]);

  return (
    <div>
      <Text noOfLines={1} fontWeight={'bold'} color={'white'} mt={'16px'} ms={"8px"}>
        {header}
      </Text>
      <Fade in={overflow && left}>
        <Circle
          size="50px"
          color={'#718096'}
          sx={{
            position: 'absolute',
            left: '0px',
            top: `${containerBounds.offsetTop}px`,
            backgroundColor: '#1a202c',
          }}
          onClick={handleScrollLeft}
        >
          <ArrowBackIosIcon />
        </Circle>
      </Fade>
      <Fade in={overflow && !left}>
        <Circle
          size="50px"
          color={'#718096'}
          sx={{
            position: 'absolute',
            right: '0px',
            top: `${containerBounds.offsetTop}px`,
            backgroundColor: ' #1a202c',
          }}
          onClick={handleScrollRight}
        >
          <ArrowForwardIosIcon />
        </Circle>
      </Fade>
      <div
        ref={containerRef}
        {...bind()}
        css={css`
          display: flex;
          overflow-x: scroll;
          overflow-y: hidden;
          width: 100%;
          padding: 10px;
          &::-webkit-scrollbar {
            width: 0px;
          }
        `}
      >
        {data.map(datum => {
          return (
            <div key={datum[idKey]}>
              <Item key={datum[idKey]} {...datum} />
            </div>
          );
        })}
        {data.length === 0 && Array.from([1, 2]).map(item => placeholder(item))}
      </div>
    </div>
  );
}
